import React from 'react';
import {
  Button,
  Link,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Person24Regular } from '@fluentui/react-icons';
import { useAuth } from '../context/BaseAuthProvider';
import { useEditor } from '../editor/EditorContext';
import { featureFlags } from '@cuntas-mitool/shared';

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingHorizontalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  headline: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase400,
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase300,
  },
  featureList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
  },
  tick: {
    color: tokens.colorPaletteGreenForeground1,
    fontWeight: tokens.fontWeightSemibold,
    flexShrink: 0,
  },
  trialCard: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
    border: `1px solid ${tokens.colorPaletteGreenBorder2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalS,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  trialTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorPaletteGreenForeground1,
    fontSize: tokens.fontSizeBase300,
  },
  trialSubtitle: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground2,
  },
  signInLink: {
    textAlign: 'center',
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    display: 'block',
  },
  fullWidth: {
    width: '100%',
  },
  contactFooter: {
    textAlign: 'center',
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground4,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingTop: tokens.spacingVerticalS,
    display: 'block',
  },
});

export const PreAuthWelcome: React.FC = () => {
  const styles = useStyles();
  const { login } = useAuth();
  const { host } = useEditor();
  const isGoogleDocs = host === 'gdocs';
  const primaryProvider: 'microsoft' | 'google' = isGoogleDocs ? 'google' : 'microsoft';

  return (
    <div className={styles.container}>
      <Text className={styles.headline}>
        Browse the Irish Primary School Curriculum in {isGoogleDocs ? 'Google Docs' : 'Word'}
      </Text>
      <Text className={styles.description}>
        Select your class, subject, and strand — then click any learning outcome
        to insert it directly into your document.
      </Text>

      <div className={styles.featureList}>
        {[
          'Full Irish Primary School Curriculum',
          'One-click insert into your document',
          isGoogleDocs ? 'Sign in with your Google account' : 'Sign in with your Microsoft account',
        ].map((feature) => (
          <div key={feature} className={styles.featureItem}>
            <Text className={styles.tick}>✓</Text>
            <Text>{feature}</Text>
          </div>
        ))}
      </div>

      <div className={styles.trialCard}>
        <Text className={styles.trialTitle}>7-Day Free Trial</Text>
        <Text className={styles.trialSubtitle}>
          Full access to every feature.
        </Text>
      </div>

      <Button
        appearance="primary"
        icon={<Person24Regular />}
        onClick={() => login(primaryProvider)}
        className={styles.fullWidth}
      >
        Sign up with {isGoogleDocs ? 'Google' : 'Microsoft'}
      </Button>

      {!isGoogleDocs && featureFlags.googleAuth && (
        <Button
          appearance="outline"
          onClick={() => login('google')}
          className={styles.fullWidth}
        >
          Sign up with Google
        </Button>
      )}

      <Text className={styles.signInLink}>
        Returning user?{' '}
        <Link style={{ fontSize: 'inherit' }} onClick={() => login(primaryProvider)}>Sign in</Link>
      </Text>

      <Text className={styles.contactFooter}>
        Need help? contact@cuntasmitool.ie
      </Text>
    </div>
  );
};

export default PreAuthWelcome;
